import {
  type Category,
  costPerCanonical,
  type Dimension,
  dec,
  formatQuantity,
  formatUnitCost,
  isDecimalString,
  type Pack,
  toCanonical,
  type UnitId,
  ZERO,
} from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useMemo, useState } from "react";
import {
  type Item,
  type MovementInput,
  send,
  useBorrowing,
  useChange,
  useItem,
  useItems,
  useLocations,
  useSuppliers,
} from "../api";
import {
  Dialog,
  DialogForm,
  ErrorNote,
  Field,
  PriceInput,
  QuantityInput,
  type QuantityUnit,
} from "../components/ui";
import { hopDetail, mainSourceOf, showOnHand, showQuantity, today } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import { useNotify } from "../notify";
import { BrandInput } from "./BrandInput";
import { AddItemDialog } from "./ItemDialogs";
import { ItemPicker } from "./ItemPicker";
import { type PackChoice, PackPicker, packChoiceOf, usablePack } from "./PackPicker";
import { RegionInput } from "./RegionInput";
import { collectSpecs, SpecFields, useSpecDrafts } from "./SpecFields";

export type MovementMode =
  | "receive"
  | "use"
  | "waste"
  | "adjust"
  | "transfer"
  | "borrow-in"
  | "borrow-repay"
  | "lend-out"
  | "lend-return";

const TITLES: Record<MovementMode, string> = {
  receive: "Receive stock",
  use: "Use stock",
  waste: "Dump stock",
  adjust: "Count stock",
  transfer: "Move stock",
  "borrow-in": "Borrow from another brewery",
  "borrow-repay": "Pay back a loan",
  "lend-out": "Lend to another brewery",
  "lend-return": "Lent stock came back",
};

const SUBMIT: Record<MovementMode, string> = {
  receive: "Receive",
  use: "Record use",
  waste: "Record dump",
  adjust: "Save count",
  transfer: "Move",
  "borrow-in": "Record loan",
  "borrow-repay": "Record payback",
  "lend-out": "Record loan",
  "lend-return": "Record return",
};

const INBOUND = new Set<MovementMode>(["receive", "borrow-in", "lend-return"]);

/** Local date plus the current time, so a backdated entry still sorts sensibly within its day. */
function occurredAt(date: string): string | undefined {
  if (!date || date === today()) return undefined;
  const now = new Date();
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d, now.getHours(), now.getMinutes()).toISOString();
}

export function MovementDialog({
  open,
  onClose,
  mode,
  itemId,
  counterparty: initialCounterparty,
}: {
  open: boolean;
  onClose: () => void;
  mode: MovementMode;
  itemId?: string;
  counterparty?: string;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={TITLES[mode]}>
      {open && mode === "adjust" && !itemId ? (
        <p className="count-sheet-link">
          <Link to="/count" onClick={onClose}>
            Count a whole location on one sheet
          </Link>
        </p>
      ) : null}
      {open ? (
        <MovementForm
          mode={mode}
          itemId={itemId}
          onDone={onClose}
          initialCounterparty={initialCounterparty}
        />
      ) : null}
    </Dialog>
  );
}

function MovementForm({
  mode,
  itemId: fixedItemId,
  onDone,
  initialCounterparty,
}: {
  mode: MovementMode;
  itemId?: string;
  onDone: () => void;
  initialCounterparty?: string;
}) {
  const { layout } = useLayout();
  const { data: items = [] } = useItems();
  // The fields choose their starting location once, so they wait for the list: opened from a page that
  // had not loaded it (Borrowing), the location showed but was blank, and the dialog would not submit.
  const { data: locations, error: locationsError } = useLocations();
  // Kept whole, so one just added to inventory is there before the list has it.
  const [picked, setPicked] = useState<Item | null>(null);
  // What was typed when New item was pressed, while the Add item dialog is open.
  const [adding, setAdding] = useState<string | null>(null);
  const item = fixedItemId
    ? items.find((i) => i.id === fixedItemId)
    : picked
      ? (items.find((i) => i.id === picked.id) ?? picked)
      : undefined;
  const picker = fixedItemId ? null : (
    <Field label="Item" required>
      {item ? (
        <div className="picked-item">
          <strong>{item.displayName}</strong>
          {hopDetail(item) ? <span className="muted">{hopDetail(item)}</span> : null}
          <span className="faint num">
            {dec(item.onHand.quantity).gt(ZERO)
              ? `${showOnHand(item, layout.inventory.quantityMode)} on hand`
              : "none on hand"}
          </span>
          <button type="button" className="button small" onClick={() => setPicked(null)}>
            Change
          </button>
        </div>
      ) : (
        <ItemPicker onPick={setPicked} onCreate={setAdding} />
      )}
    </Field>
  );
  // Outside the movement's own form, which would otherwise submit along with the new item's.
  const addItem =
    adding !== null ? (
      <AddItemDialog
        open
        onClose={() => setAdding(null)}
        onAdded={setPicked}
        initialQuery={adding}
        offerInvoice={false}
      />
    ) : null;
  if (!item || !locations) {
    return (
      <>
        <DialogForm
          onSubmit={() => {}}
          footer={
            <>
              <button type="button" className="button" onClick={onDone}>
                Cancel
              </button>
              <button type="submit" className="button primary" disabled>
                {SUBMIT[mode]}
              </button>
            </>
          }
        >
          {picker}
          <ErrorNote error={locationsError} />
        </DialogForm>
        {addItem}
      </>
    );
  }
  return (
    <>
      <MovementFields
        key={item.id}
        mode={mode}
        item={item}
        picker={picker}
        onDone={onDone}
        initialCounterparty={initialCounterparty}
      />
      {addItem}
    </>
  );
}

function MovementFields({
  mode,
  item,
  picker,
  onDone,
  initialCounterparty,
}: {
  mode: MovementMode;
  item: Item;
  picker: ReactNode;
  onDone: () => void;
  initialCounterparty?: string;
}) {
  const { layout } = useLayout();
  const notify = useNotify();
  const { data: locations = [] } = useLocations();
  const { data: suppliers = [] } = useSuppliers();
  const { data: detail } = useItem(item.id);
  const { data: balances = [] } = useBorrowing();
  const live = locations.filter((l) => !l.archived);

  const stockAt = (locationId: string) =>
    item.onHand.byLocation.find((b) => b.locationId === locationId)?.quantity ?? "0";
  const fullest = [...item.onHand.byLocation].sort((a, b) =>
    dec(b.quantity).cmp(dec(a.quantity)),
  )[0]?.locationId;

  const [locationId, setLocationId] = useState(fullest ?? live[0]?.id ?? "");
  const [toLocationId, setToLocationId] = useState(
    live.find((l) => l.id !== (fullest ?? live[0]?.id))?.id ?? "",
  );
  const defaultUnit: QuantityUnit =
    item.pack && (INBOUND.has(mode) || layout.inventory.quantityMode === "pack")
      ? "pack"
      : item.displayUnit;
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState<QuantityUnit>(defaultUnit);
  const [lotId, setLotId] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(today());
  const [counterparty, setCounterparty] = useState(initialCounterparty ?? "");

  // Receipt details
  const [lotCode, setLotCode] = useState("");
  // Prefilled with who and what SKU was used last time, or the catalog's when there is no receipt yet.
  const [supplierId, setSupplierId] = useState(item.defaultSupplier?.id ?? "");
  const [sku, setSku] = useState(item.defaultSupplierSku ?? "");
  const [cropYear, setCropYear] = useState("");
  // Blank: the brand on the item number it was bought under, which the server knows.
  const [brand, setBrand] = useState("");
  // Blank: the region on the item number, where it states one; the grower, nobody's guess.
  const [region, setRegion] = useState("");
  const [grower, setGrower] = useState("");
  const [bestBy, setBestBy] = useState("");
  // What this delivery came in: the item's pack to start with, which is what it came in last time.
  const [pack, setPack] = useState<PackChoice>(
    packChoiceOf(item.pack, item.dimension as Dimension),
  );
  // A delivery starts from the item's specs and keeps whatever it is received with.
  const { drafts, set: setDraft } = useSpecDrafts(item.specs, item.category as Category);
  const mainSource = mainSourceOf(item.specs);
  const [specError, setSpecError] = useState<string | null>(null);
  const [priceAmount, setPriceAmount] = useState("");
  const [pricePer, setPricePer] = useState<QuantityUnit>(item.pack ? "pack" : item.displayUnit);

  const inbound = INBOUND.has(mode);
  const isHop = item.category === "hop";
  const isYeast = item.category === "yeast";
  const lotsHere = (detail?.lots ?? []).filter((l) =>
    l.byLocation.some((b) => b.locationId === locationId),
  );
  const lotPack = (id: string): Pack | null => detail?.lots.find((l) => l.id === id)?.pack ?? null;
  const counterparties = [...new Set(balances.map((b) => b.counterparty))];

  const change = useChange((body: MovementInput, key) => send.movement(body, key), {
    onSuccess: (result) => {
      notify({
        title:
          result.movements.length === 0
            ? `Count matches for ${item.displayName}. Nothing to change.`
            : `${TITLES[mode].replace(/ stock$/, "")}: ${item.displayName}`,
        link: { kind: "item", itemId: item.id },
      });
      onDone();
    },
  });

  const receiptPack: Pack | null = useMemo(() => {
    const usable = usablePack(pack);
    if (!usable) return null;
    try {
      return { name: usable.name, size: toCanonical(usable.size, item.dimension as Dimension) };
    } catch {
      return null;
    }
  }, [pack, item.dimension]);
  const priceNote = useMemo(() => {
    if (!isDecimalString(priceAmount)) return null;
    try {
      const perCanonical = costPerCanonical(
        { amount: priceAmount, per: pricePer },
        item.dimension as Dimension,
        receiptPack,
      );
      const other =
        pricePer === "pack"
          ? formatUnitCost(perCanonical, { unit: item.displayUnit })
          : receiptPack
            ? formatUnitCost(perCanonical, { pack: receiptPack })
            : null;
      return other ? `= ${other}` : null;
    } catch (error) {
      return error instanceof Error ? error.message : null;
    }
  }, [priceAmount, pricePer, item, receiptPack]);
  const packForUnit: Pack | null = INBOUND.has(mode)
    ? receiptPack
    : lotId
      ? lotPack(lotId)
      : item.pack;
  /** The entered amount in canonical units, or null while it is not a usable number. Zero allowed. */
  const canonical = useMemo(() => {
    if (!isDecimalString(amount) || dec(amount).lt(ZERO)) return null;
    try {
      return toCanonical({ value: amount, unit }, item.dimension as Dimension, packForUnit);
    } catch {
      return null;
    }
  }, [amount, unit, item, packForUnit]);
  const preview = canonical !== null && dec(canonical).gt(ZERO) ? canonical : null;

  const expected = stockAt(locationId);
  const countDelta = canonical !== null ? dec(canonical).minus(dec(expected)) : null;

  function lotInput() {
    const changes = collectSpecs(drafts);
    return {
      lotCode: lotCode.trim() || undefined,
      supplierId: supplierId || undefined,
      supplierSku: sku.trim() || undefined,
      receivedOn: date,
      cropYear: cropYear ? Number(cropYear) : undefined,
      brand: isHop && brand.trim() ? brand.trim() : undefined,
      region: isHop && region.trim() ? region.trim() : undefined,
      grower: isHop && grower.trim() ? grower.trim() : undefined,
      bestBy: bestBy || undefined,
      price:
        mode === "receive" && isDecimalString(priceAmount)
          ? { amount: priceAmount, per: pricePer }
          : undefined,
      // Always said, so a lot records exactly what it came in; null means it did not come in packs.
      pack: usablePack(pack),
      specs: changes.specs.length ? changes.specs : undefined,
    };
  }

  function submit() {
    const changes = collectSpecs(drafts);
    setSpecError(changes.error);
    if (changes.error) return;
    const quantity = { value: amount, unit };
    const common = {
      itemId: item.id,
      note: note.trim() || undefined,
      occurredAt: occurredAt(date),
    };
    const lot = lotId || undefined;
    let body: MovementInput;
    switch (mode) {
      case "receive":
        body = { kind: "receive", ...common, locationId, quantity, lot: lotInput() };
        break;
      case "use":
      case "waste":
        body = { kind: mode, ...common, locationId, quantity, lotId: lot };
        break;
      case "adjust":
        body = { kind: "adjust", ...common, locationId, counted: quantity, expected };
        break;
      case "transfer":
        body = {
          kind: "transfer",
          ...common,
          fromLocationId: locationId,
          toLocationId,
          quantity,
          lotId: lot,
        };
        break;
      default: {
        const [kind, direction] = mode.split("-") as ["borrow" | "lend", string];
        const inboundLot = INBOUND.has(mode) ? lotInput() : undefined;
        body =
          kind === "borrow"
            ? {
                kind,
                direction: direction as "in" | "repay",
                ...common,
                locationId,
                counterparty: counterparty.trim(),
                quantity,
                lotId: lot,
                lot: inboundLot,
              }
            : {
                kind,
                direction: direction as "out" | "return",
                ...common,
                locationId,
                counterparty: counterparty.trim(),
                quantity,
                lotId: lot,
                lot: inboundLot,
              };
      }
    }
    change.mutate(body);
  }

  const canSubmit =
    locationId &&
    (mode === "adjust" ? canonical !== null : preview !== null) &&
    (mode !== "transfer" || (toLocationId && toLocationId !== locationId)) &&
    (!mode.startsWith("borrow") && !mode.startsWith("lend") ? true : counterparty.trim());

  const footer = (
    <>
      <button type="button" className="button" onClick={onDone}>
        Cancel
      </button>
      <button type="submit" className="button primary" disabled={!canSubmit || change.isPending}>
        {change.isPending ? "Saving" : SUBMIT[mode]}
      </button>
    </>
  );

  return (
    <DialogForm onSubmit={() => canSubmit && !change.isPending && submit()} footer={footer}>
      {picker}
      {live.length === 0 ? (
        <div className="notice warn">
          There are no storage locations yet. Every movement names one, so{" "}
          <Link to="/locations" onClick={onDone}>
            add a location
          </Link>{" "}
          first.
        </div>
      ) : null}
      <div className="form-grid">
        {mode.startsWith("borrow") || mode.startsWith("lend") ? (
          <Field label="Brewery" required className="wide">
            <input
              className="input"
              list="counterparties"
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              placeholder="Which brewery"
              required
            />
            <datalist id="counterparties">
              {counterparties.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
        ) : null}

        <Field label={mode === "transfer" ? "From" : "Location"} required>
          <select
            className="select"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          >
            {live.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {dec(stockAt(l.id)).eq(ZERO)
                  ? ""
                  : ` (${showQuantity(item, stockAt(l.id), layout.inventory.quantityMode)})`}
              </option>
            ))}
          </select>
        </Field>

        {mode === "transfer" ? (
          <Field label="To" required>
            <select
              className="select"
              value={toLocationId}
              onChange={(e) => setToLocationId(e.target.value)}
            >
              <option value="">Choose</option>
              {live
                .filter((l) => l.id !== locationId)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </select>
          </Field>
        ) : null}

        <Field
          label={mode === "adjust" ? "Counted" : "Amount"}
          required
          hint={
            mode === "adjust"
              ? `On record here: ${showQuantity(item, expected, "unit")}${
                  item.pack ? ` (${showQuantity(item, expected, "pack")})` : ""
                }`
              : preview && unit === "pack"
                ? `= ${showQuantity(item, preview, "unit")}`
                : undefined
          }
        >
          <QuantityInput
            value={amount}
            unit={unit}
            dimension={item.dimension as Dimension}
            packName={packForUnit?.name ?? (INBOUND.has(mode) ? null : item.pack?.name) ?? null}
            onChange={(v, u) => {
              setAmount(v);
              setUnit(u);
            }}
            autoFocus
            required
          />
        </Field>

        {mode === "use" ||
        mode === "waste" ||
        mode === "transfer" ||
        mode === "borrow-repay" ||
        mode === "lend-out" ? (
          <Field label="Lot" hint="Oldest first unless you pick one">
            <select className="select" value={lotId} onChange={(e) => setLotId(e.target.value)}>
              <option value="">Oldest first</option>
              {lotsHere.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.lotCode} ·{" "}
                  {l.receivedOn ? `received ${l.receivedOn}` : "received date not known"}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {mode === "adjust" && countDelta !== null ? (
          <div className={`notice wide ${countDelta.lt(ZERO) ? "warn" : ""}`}>
            {countDelta.eq(ZERO)
              ? "Matches the record. Nothing will change."
              : `Records a correction of ${countDelta.gt(ZERO) ? "+" : ""}${formatQuantity(
                  countDelta.toFixed(),
                  { unit: item.displayUnit as UnitId },
                )}.`}
          </div>
        ) : null}

        {inbound ? (
          <>
            <Field label="Lot code" hint="Leave blank to use the received date">
              <input
                className="input"
                value={lotCode}
                onChange={(e) => setLotCode(e.target.value)}
                placeholder={date}
              />
            </Field>
            {mode === "receive" ? (
              <>
                <Field label="Supplier">
                  <select
                    className="select"
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                  >
                    <option value="">Not recorded</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Supplier SKU">
                  <input className="input" value={sku} onChange={(e) => setSku(e.target.value)} />
                </Field>
                <Field
                  label="Price"
                  hint={priceNote ?? "From the invoice. Shipping, tax and fees stay off it."}
                >
                  <PriceInput
                    amount={priceAmount}
                    per={pricePer}
                    onChange={(amount, per) => {
                      setPriceAmount(amount);
                      setPricePer(per);
                    }}
                    dimension={item.dimension as Dimension}
                    packName={receiptPack?.name ?? null}
                  />
                </Field>
              </>
            ) : null}
            <Field label="Came in" hint="This delivery's pack; the next one starts from it">
              <PackPicker
                dimension={item.dimension as Dimension}
                category={item.category as Category}
                displayUnit={item.displayUnit}
                value={pack}
                onChange={(next) => {
                  setPack(next);
                  // Counted in packs of nothing is not a count: fall back to the item's unit.
                  if (!usablePack(next) && unit === "pack") setUnit(item.displayUnit);
                }}
              />
            </Field>
            {isHop ? (
              <Field label="Crop year">
                <input
                  className="input num"
                  inputMode="numeric"
                  value={cropYear}
                  onChange={(e) => setCropYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder={String(new Date().getFullYear() - 1)}
                />
              </Field>
            ) : null}
            {isHop ? (
              <Field
                label="Brand"
                hint="The hop company on the bag, if it isn't the item number's."
              >
                <BrandInput
                  value={brand}
                  onChange={setBrand}
                  placeholder="As the item number says"
                />
              </Field>
            ) : null}
            {isHop ? (
              <Field label="Region" hint="Where they were grown, if the bag or item number says.">
                <RegionInput
                  value={region}
                  onChange={setRegion}
                  placeholder="As the item number says, if it does"
                />
              </Field>
            ) : null}
            {isHop ? (
              <Field label="Grower" hint="The farm, if anyone knows it.">
                <input
                  className="input"
                  value={grower}
                  onChange={(e) => setGrower(e.target.value)}
                />
              </Field>
            ) : null}
            {isYeast || item.category === "adjunct" ? (
              <Field label="Best by">
                <input
                  className="input"
                  type="date"
                  value={bestBy}
                  onChange={(e) => setBestBy(e.target.value)}
                />
              </Field>
            ) : null}
          </>
        ) : null}

        <Field label={mode === "receive" ? "Received on" : "Date"}>
          <input
            className="input"
            type="date"
            value={date}
            max={today()}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="Note" className="wide">
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      {mode === "receive" && drafts.length > 0 ? (
        <>
          <h3 style={{ margin: "4px 0 0", fontSize: 14 }}>Specs for this delivery</h3>
          <p className="muted" style={{ margin: 0 }}>
            {mainSource ? `Source: ${mainSource}. ` : ""}Modify to set lot values.
          </p>
          <SpecFields drafts={drafts} onChange={setDraft} mainSource={mainSource} />
        </>
      ) : null}
      {specError ? <div className="form-error">{specError}</div> : null}
      <ErrorNote error={change.error} />
    </DialogForm>
  );
}

/** Canonical units in one display unit, for showing a pack size in pounds rather than grams. */
function _unitFactor(item: Item): string {
  return toCanonical({ value: "1", unit: item.displayUnit }, item.dimension as Dimension);
}
